import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Styled from "./styles";
import { useLogout } from "../../services/mutations";
import { isUserLoggedIn } from "../../utils/helpers";
import { ASSETS, PATH } from "../../utils/config";
import {
  getGenericWheelSegments,
  GENERIC_WHEEL_USER_ID,
} from "../../constants/genericWheel";
import { logout } from "../../redux/actions";
import { purgePersistedState } from "../../redux";
import { TextView } from "@components";
import ReferralsIcon from "assets/images/referrals-icon.svg";
import grid from "@assets/images/grid.svg";
import log_out from "@assets/images/log_out.svg";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AccountBoxIcon from "@mui/icons-material/AccountBox";
import LoginIcon from "@mui/icons-material/Login";
import { useLocation } from "react-router-dom";
import { resetGallery } from "../../redux/reducers/gallery";
import { clearGalleryPopup } from "../../redux/reducers/galleryPopup";
import { getLoggedInUser } from "../../utils/helpers";
import { useQueryClient } from "react-query";
import { getHasUnsavedChanges } from "../../redux/selectors/segmentBuilderSelectors";
import { clearAllAiWheels } from "../../redux/reducers/aiWheels";
import WheelComponent from "../WheelComponent";
import { useIfUserEligible } from "../../hooks/useIfUserEligible";
import { FEATURES } from "../../constants/features";
import AccountSwitcherSection from "./AccountSwitcherSection";
import { ACCOUNT_SWITCHER_ENABLED } from "./accountSwitcherFlag";
import { getLoggedInUserToken } from "../../utils/helpers";
import AvatarLinkedWheelsSection from "./AvatarLinkedWheelsSection";
import WorkspacesOutlinedIcon from "@mui/icons-material/WorkspacesOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { TEAMS_ENABLED } from "../../pages/Teams/flag";
import { HELP_ASSISTANT_ENABLED } from "../HelpAssistant/constants";
import useHelpFabDismissed from "../HelpAssistant/useHelpFabDismissed";
import { setFabDismissed } from "../HelpAssistant/fabPersistence";
import {
  REFERRAL_PROGRAM_SUSPENDED,
  GALLERY_THEMING_ENABLED,
  MEDIA_LIBRARY_PICKER_ENABLED,
  ANALYTICS_ENABLED,
} from "../../constants/featureFlags";
import earningsMenuLabel from "./earningsMenuLabel";

/**
 * The creator EARNINGS / referral program is SUSPENDED (Gordon, 2026-07-24, observed
 * live during a QA session: "The Earnings Portal popup should not be showing up for
 * now - we suspended the earnings program").
 *
 * While this is true the avatar menu never surfaces "Earnings Portal" — the entry
 * reads "Analytics" for every user, including those still flagged as in the referral
 * program.
 *
 * The switch itself now lives in constants/featureFlags as the ONE shared
 * REFERRAL_PROGRAM_SUSPENDED primitive (G-118) — it used to be a const local to this
 * file, which meant it governed this dropdown entry and nothing else while a dozen
 * other surfaces kept advertising the suspended program. Import it; never re-declare
 * a second referral switch here.
 *
 * TO RESUME THE PROGRAM: REACT_APP_REFERRAL_PROGRAM=true. The underlying referral
 * membership (`isInReferralProgram`) is untouched, so nothing is lost while suspended.
 */
const EARNINGS_PROGRAM_SUSPENDED = REFERRAL_PROGRAM_SUSPENDED;

// The header avatar sits in a shrink-wrapped UserContainer (no explicit
// width/height). `width/height: 100%` then resolves to 0×0 — MEASURED on
// View Wheel /wheel/:id: [aria-label="Account menu"] was 10×5 and the
// WheelComponent box 0×0, so the profile vanished. Pin both axes to the
// same 46px clamp the width= prop already uses.
const AVATAR_WHEEL_BOX = {
  width: "46px",
  height: "46px",
  maxWidth: "46px",
  maxHeight: "46px",
  marginRight: 10,
  overflow: "hidden",
};

const ProfileMenu = React.forwardRef(
  (
    {
      setModalOpen = null,
      setOnLeave = null,
      onClickBeta = () => {},
      className,
      isJoyrideOpen = false,
      wheelUpdatedDataSorted = [],
      isModalOpen = false, // New prop
    },
    ref
  ) => {
    const { userProfile } = useSelector((state) => state.userProfile);
    const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
    const [showDropdown, setShowDropdown] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [clicked, setClicked] = useState(false);
    const [isLoggingOut, setisLoggingOut] = useState(false);
    const location = useLocation();

    const userType = userProfile?.referral_type;
    const { isInReferralProgram } = useIfUserEligible(FEATURES.REFFERAL_PROGRAM);
    let nav = useNavigate();
    const dispatch = useDispatch();
    let userLogout = useLogout();
    const dropdownRef = useRef();
    const queryClient = useQueryClient();

    // D-01: the 0-Wheels avatar is a generic iYuga Wheel (six equal brand
    // segments) rendered by the real WheelComponent — not a plain avatar.
    const genericWheelSegments = useMemo(() => getGenericWheelSegments(), []);

    // Gordon 2026-07-30 (asked three times): the floating Help launcher is
    // dismissable, and THIS menu is the only way back. The entry appears ONLY
    // while the FAB is hidden for this user — a permanently-present "Help" item
    // that does nothing when the FAB is already on screen would be a dead
    // control (G-54). Subscribed, not read once: dismissing the FAB is a
    // sibling component's write, and this menu must see it without a reload.
    const helpFabUserId = getLoggedInUser()?.id ?? null;
    const helpFabDismissed = useHelpFabDismissed(helpFabUserId);

    const handleLogout = () => {
      setisLoggingOut(true);
      userLogout.mutate(null, {
        onSuccess: (response) => {
          setisLoggingOut(false);
          dispatch(logout());
          dispatch(resetGallery());
          dispatch(clearGalleryPopup());
          dispatch(clearAllAiWheels());
          localStorage.removeItem("keyCloak");
          sessionStorage.removeItem("referralData");
          purgePersistedState(); // EW-1004: clear persist:root remnant
          queryClient.clear();
          nav("/login");
        },
        onError: (err) => {
          setisLoggingOut(false);
        },
      });
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target)
        ) {
          // If a click is outside the dropdown
          if (isModalOpen || isJoyrideOpen) {
            // If a modal is open or Joyride step active, do NOT close the dropdown
            return;
          }
          // If click target is one of the Joyride upgrade links, ignore
          const ignoreIds = [
            "premium-link",
            "prosuite-link",
            "upgrading-link",
            "premium-earnings-link",
            "prosuite-earnings-link",
          ];
          if (ignoreIds.includes(event.target.id)) {
            return;
          }
          // Otherwise, close the dropdown
          setShowDropdown(false);
          setClicked(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [dropdownRef, isModalOpen]);

    useEffect(() => {
      if (!showDropdown) {
        setActiveIndex(-1);
      } else if (isJoyrideOpen) {
        setActiveIndex(1);
      }
    }, [showDropdown, isJoyrideOpen]);

    const handleDropdownItemClick = (action) => {
      if (hasUnsavedChanges && setOnLeave && setModalOpen) {
        setOnLeave(() => action);
        setModalOpen(true);
        setShowDropdown(false);
      } else {
        action();
        setShowDropdown(false);
      }
    };

    React.useImperativeHandle(ref, () => ({
      toggleDropdown: () => {
        setClicked(true);
        setShowDropdown(!showDropdown);
      },
      closeDropdown: () => {
        setClicked(false);
        setShowDropdown(false);
      },
    }));

    return (
      <Styled.UserContainer
        clicked={clicked}
        ref={dropdownRef}
        className={className}
        // A7 (a11y): the avatar is the primary account/navigation menu trigger
        // (Gallery, Settings, Discover, Teams, Logout…). It must be reachable and
        // operable by keyboard and announced to screen readers, not a click-only
        // <div>. One handler for pointer + keyboard so the paths can't diverge.
        role="button"
        tabIndex={0}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={showDropdown}
        onClick={() => {
          setClicked(true);
          setShowDropdown(!showDropdown);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setClicked(true);
            setShowDropdown((prev) => !prev);
          }
        }}
      >
        {" "}
        {wheelUpdatedDataSorted && wheelUpdatedDataSorted?.length > 0 ? (
          <WheelComponent
            userData={{
              id: getLoggedInUser()?.id ?? GENERIC_WHEEL_USER_ID,
              url: userProfile?.profile_image || ASSETS.iyugaLogo,
            }}
            maxSelectedArcs={0}
            arcThickness={0}
            chartData={wheelUpdatedDataSorted}
            margin={1}
            // Match the wheel's internal geometry to the clamped 46px avatar.
            // Without this the decorative portrait-background circle sizes off
            // the default width="40vw" (calc(40vw*0.6)=24vw≈288px absolute),
            // escaping the 46px avatar and forcing a page-wide horizontal
            // scrollbar on the gallery.
            width="46px"
            setChartData={() => {}}
            containerDivStyle={AVATAR_WHEEL_BOX}
            config={{
              showTooltips: false,
            }}
          />
        ) : (
          // D-01: 0 Wheels -> generic iYuga Wheel (six equal brand segments)
          // via the real renderer, profile image / logo in the center (no hollow
          // center). Geometry matches the has-Wheels 46px avatar frame above.
          <WheelComponent
            userData={{
              id: GENERIC_WHEEL_USER_ID,
              url: userProfile?.profile_image || ASSETS.iyugaLogo,
            }}
            maxSelectedArcs={0}
            arcThickness={0}
            chartData={genericWheelSegments}
            margin={1}
            width="46px"
            setChartData={() => {}}
            containerDivStyle={AVATAR_WHEEL_BOX}
            config={{
              showTooltips: false,
            }}
          />
        )}
        {showDropdown ? (
          !isUserLoggedIn() ? (
            <>
              <Styled.Dropdown>
                <Styled.DropdownItem
                  onClick={() => handleDropdownItemClick(() => nav("/login"))}
                >
                  <LoginIcon style={{ fontSize: "15px", marginRight: "5px" }} />
                  Sign in
                </Styled.DropdownItem>
                <Styled.DropdownItem
                  onClick={
                    () => nav("/signup")
                    // handleDropdownItemClick(onClickBeta)
                  }
                >
                  <AccountBoxIcon
                    style={{
                      fontSize: "18px",
                      marginTop: "1px",
                      marginRight: "5px",
                    }}
                  />
                  Sign up for Beta
                </Styled.DropdownItem>
              </Styled.Dropdown>
            </>
          ) : (
            <Styled.Dropdown>
              {/* Gordon 2026-07-20: GitHub-style account switcher at the TOP of the
                  menu (a distinct hunk from PR #2090's linked-wheels section near
                  the Logout divider, so the two compose without colliding).
                  Self-gates on REACT_APP_ACCOUNT_SWITCHER; when OFF nothing renders
                  and the account registry is never written. */}
              {ACCOUNT_SWITCHER_ENABLED && (
                <AccountSwitcherSection
                  currentUserId={getLoggedInUser()?.id}
                  keyCloak={{
                    token: getLoggedInUserToken(),
                    user: getLoggedInUser(),
                  }}
                  userProfile={userProfile}
                  onRequestClose={() => {
                    setShowDropdown(false);
                    setClicked(false);
                  }}
                />
              )}
              {location.pathname !== "/gallery" && (
                <Styled.DropdownItem
                  onClick={() =>
                    handleDropdownItemClick(() =>
                      nav(`/gallery/${getLoggedInUser().id}`)
                    )
                  }
                >
                  {/* Gordon 2026-08-18: the Gallery is the user's home base,
                      so this row's icon is the line-art Home/House glyph
                      (matching the new header Home nav entry), replacing the
                      old grid/gallery glyph. Label stays "Gallery". */}
                  {/* Gordon 2026-08-18: the Gallery is the user's home base,
                      so this row's icon is the line-art Home/House glyph
                      (matching the new header Home nav entry), replacing the
                      old grid/gallery glyph. Label stays "Gallery". */}
                  <HomeOutlinedIcon
                    aria-hidden="true"
                    style={{ fontSize: 18, marginRight: 8, marginLeft: -2, color: "#667085" }}
                  />{" "}
                  <TextView>Gallery</TextView>
                </Styled.DropdownItem>
              )}
              {/* QA-112 (Gordon 2026-08-11): "Themes" section -- the Gallery
                  Theme control moved OUT of the Gallery profile card's kebab
                  menu into here. This is where Gallery, Business Box, and
                  LinkVault themes will all eventually live; for now it holds
                  ONLY the Gallery theme (JUST MOVE, no extra scope). Same
                  flag-gate the old kebab item used, so this is byte-identical
                  to today when GALLERY_THEMING_ENABLED is OFF. Navigates to
                  the signed-in user's own gallery with `?theme=gallery`,
                  which Profile/index.jsx picks up to reopen the same
                  Customize hub (GallerySettings tab "2") the old item did. */}
              {GALLERY_THEMING_ENABLED && (
                <>
                  <div
                    role="group"
                    aria-label="Themes"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px 2px",
                    }}
                  >
                    <PaletteOutlinedIcon
                      aria-hidden="true"
                      style={{ fontSize: 16, color: "#667085" }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        color: "#667085",
                      }}
                    >
                      Themes
                    </span>
                  </div>
                  <Styled.DropdownItem
                    onClick={() =>
                      handleDropdownItemClick(() =>
                        nav(`/gallery/${getLoggedInUser().id}?theme=gallery`)
                      )
                    }
                  >
                    <Styled.DropdownImage src={grid} alt="" />{" "}
                    <TextView>Gallery Theme</TextView>
                  </Styled.DropdownItem>
                </>
              )}
              {/* stg-0918-hide-themes-media-library-analytics-from-dropdown-now:
                  keeps this row BYTE-IDENTICAL to today when
                  MEDIA_LIBRARY_PICKER_ENABLED is OFF (staging only; dev/sandbox
                  keep it ON). Route itself is also guarded in routes.js so a
                  deep link can't reach it either. */}
              {MEDIA_LIBRARY_PICKER_ENABLED && (
                <Styled.DropdownItem
                  onClick={() =>
                    handleDropdownItemClick(() => nav(PATH.MEDIA_LIBRARY))
                  }
                >
                  <Styled.DropdownImage
                    src={ASSETS.mediaSidebarIcon || grid}
                    alt="Media Library"
                  />
                  <TextView>Media Library</TextView>
                </Styled.DropdownItem>
              )}
              <Styled.DropdownItem
                onClick={() => handleDropdownItemClick(() => nav("/discover"))}
              >
                <Styled.DropdownImage3
                  style={{ height: "19px", width: "19px" }}
                  src={ASSETS.explore}
                  // Decorative — the adjacent TextView already gives this row
                  // its accessible name; a non-empty alt here would make a
                  // screen reader announce "Discover Discover" (QA-validator
                  // finding, 2026-08-06 — my earlier alt="Discover" was wrong).
                  alt=""
                />
                {/* Gordon, 2026-08-06: "the page is called Discover, not
                    Explore." Label renamed; the route (/explore) is unchanged. */}
                <TextView>Discover</TextView>
              </Styled.DropdownItem>
              <Styled.DropdownItem
                onClick={() => handleDropdownItemClick(() => nav("/settings"))}
              >
                <Styled.DropdownImage src={ASSETS.settings} alt="Settings" />
                <TextView>Settings</TextView>
              </Styled.DropdownItem>
              {/* stg-0918e-account-switcher-menu-still-shows-analytics-row-P0:
                  ANALYTICS_ENABLED is the SAME flag PATH.ANALYTICS's route
                  already gates (routes.js) — staging sets
                  REACT_APP_ANALYTICS_ENABLED=false specifically to hide this
                  row (see its docstring in constants/featureFlags.js), but
                  this row was never wired to it. Single source of truth: no
                  second flag/const introduced. Dev/sandbox leave the var
                  unset -> ANALYTICS_ENABLED defaults true -> unchanged. */}
              {ANALYTICS_ENABLED && (
                <Styled.DropdownItem
                  className="earnings-portal-dropdown-item"
                  isActive={activeIndex === 1}
                  onClick={() => handleDropdownItemClick(() => nav("/referrals"))}
                >
                  <Styled.DropdownImage2
                    src={ASSETS.Office}
                    alt="earingsportal"
                  />{" "}
                  {/* Gordon 2026-07-24 (observed live during QA): "The Earnings Portal
                      popup should not be showing up for now - we suspended the earnings
                      program." While EARNINGS_PROGRAM_SUSPENDED is true this entry always
                      reads "Analytics" and never surfaces the Earnings Portal, regardless
                      of the user's referral-program membership. Flip the constant back to
                      false to restore the previous behaviour when the program resumes —
                      the referral state itself is untouched, so nothing is lost. */}
                  <TextView>
                    {earningsMenuLabel({
                      suspended: EARNINGS_PROGRAM_SUSPENDED,
                      isInReferralProgram,
                    })}
                  </TextView>
                </Styled.DropdownItem>
              )}

              {/* P5S1-O4 (U14): Teams/Workspaces is BUILT+WIRED(BE)+POPULATED
                  (/teams renders, all workspace APIs 200) but had NO nav entry
                  anywhere in the app -- a real user could not find it. Self-gates
                  on TEAMS_ENABLED (same flag the /teams route itself checks), so
                  while the flag is OFF this renders nothing (byte-identical). */}
              {TEAMS_ENABLED && (
                <Styled.DropdownItem
                  onClick={() => handleDropdownItemClick(() => nav("/teams"))}
                >
                  <WorkspacesOutlinedIcon
                    style={{
                      fontSize: "18px",
                      marginRight: "8px",
                      marginLeft: "-2px",
                    }}
                  />
                  <TextView>Teams</TextView>
                </Styled.DropdownItem>
              )}

              {/* Gordon 2026-07-30 (x3): recovery path for a dismissed Help
                  launcher. Rendered only while the FAB is actually hidden, so
                  the menu never carries an entry that does nothing. Clicking it
                  clears the per-user dismissed flag; the FAB re-mounts at its
                  persisted position immediately (same subscribed store). */}
              {HELP_ASSISTANT_ENABLED && helpFabDismissed && (
                <Styled.DropdownItem
                  onClick={() =>
                    handleDropdownItemClick(() =>
                      setFabDismissed(helpFabUserId, false)
                    )
                  }
                >
                  <HelpOutlineIcon
                    style={{
                      fontSize: "18px",
                      marginRight: "8px",
                      marginLeft: "-2px",
                    }}
                  />
                  <TextView>Help</TextView>
                </Styled.DropdownItem>
              )}

              {/* Gordon 2026-07-20: the signed-in user's wheels that are linked to
                  each other, grouped, each navigating to that wheel. Self-gates on
                  REACT_APP_LINKED_WHEELS_ENABLED and hides when there are none, so
                  the menu never gains a dead/empty section. */}
              <AvatarLinkedWheelsSection
                userId={getLoggedInUser()?.id}
                onNavigate={(wheelId) =>
                  handleDropdownItemClick(() => nav(`/wheel/${wheelId}`))
                }
              />

              <Styled.Divider />
              <Styled.DropdownItem
                onClick={() => handleDropdownItemClick(handleLogout)}
              >
                <Styled.DropdownImage src={log_out} alt="Logout" />{" "}
                <TextView>Logout</TextView>
              </Styled.DropdownItem>
            </Styled.Dropdown>
          )
        ) : null}
      </Styled.UserContainer>
    );
  }
);

export default ProfileMenu;
